import { Injectable } from '@angular/core';
import { buildQuery, page, seed } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { MaterialAttributes } from '@albatrosdeveloper/ave-models-package';
import MaterialMaterialGroupAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/materials/material/material-material-group/material-material-group.entity';
import { takeUntil } from 'rxjs';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import { ExportContext, ExportFileFormat, ExportRange, ExportService, ExportTaskProcessor } from 'app/layout/export-overlay';
import { LOCALIZED_PROPERTIES, MATERIAL_EXPORT_ID, MATERIAL_EXPORT_TITLE } from './material.constants';
import { MaterialService } from '../material.service';

@Injectable()
export class MaterialExport {
  constructor(
    private exportService: ExportService,
    private materialService: MaterialService,
  ) {}

  private transformItem(item: MaterialAttributes) {
    return {
      externalKeys: get(item, 'externalKeys.code') || get(item, 'code', '??'),
      barcode: get(item, 'barcode', '??'),
      name: get(item, 'name', '??'),
      reference: get(item, 'reference', '??'),
      descriptionLong: get(item, 'descriptionLong', '??'),
      descriptionShort: get(item, 'descriptionShort', '??'),
      price: get(item, 'price', '??'),
      company: get(item, 'company.name', '??'),
      division: get(item, 'division.name', '??'),
      materialBrand: get(item, 'materialBrand.name', '??'),
      materialGroups: this.getMaterialGroups(get(item, 'materialGroups', [])),
      materialImage: '-',
      type: get(item, 'type.name', '??'),
      baseUnitOfMeasure: get(item, 'baseUnitOfMeasure.name', '??'),
      canMakeAnOrderWithoutStock: this.setAsActiveOrInactive(get(item, 'canMakeAnOrderWithoutStock', '-')),
      status: this.setAsActiveOrInactive(get(item, 'statusId', '-')),
    };
  }

  protected transformSplittedByImage(item: MaterialAttributes) {
    const images = get(item, 'materialImages', []);
    if (images.length > 0) {
      return get(item, 'materialImages', []).map((image) => {
        const obj = this.transformItem(item);
        obj.materialImage = ExportTaskProcessor.types.excel.image(get(image, 'image.link')) as any;
        return obj;
      });
    } else {
      return this.transformItem(item);
    }
  }

  protected setAsActiveOrInactive(value: any) {
    return ExportTaskProcessor.types.boolean(value, { trueText: 'Activo', falseText: 'Inactivo' });
  }

  private getMaterialGroups(groups: MaterialMaterialGroupAttributes[]): string {
    if (!Array.isArray(groups) || groups.length === 0) return '??';
    return groups.map((group) => group.name).join(', ');
  }

  export(format: ExportFileFormat, range: ExportRange, totalItems: number, pageSize: number, pageIndex: number) {
    const totalPages = ceil(totalItems / pageSize) + 1;
    this.exportService.task({
      id: MATERIAL_EXPORT_ID,
      title: MATERIAL_EXPORT_TITLE,
      format: format,
      iterations: range === ExportRange.AllPages ? totalPages : 1,
      start: (ctx: ExportContext) => {
        ctx.processor.localizeItemProperties(MATERIAL_EXPORT_TITLE, LOCALIZED_PROPERTIES);
      },
      next: (ctx: ExportContext) => {
        const currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
        this.materialService.findByFilter(buildQuery(
          seed(this.materialService.currentFilter), page(currentPage, pageSize)
        ), { contextHeader: 'export' }).pipe(takeUntil(ctx.stopped)).subscribe((response) => {
          get(response, 'data.0.data', []).map((item) => {
            ctx.processor.addToSection(MATERIAL_EXPORT_TITLE, this.transformSplittedByImage(item));
          });
          ctx.next();
        });
      },
    }).start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(MATERIAL_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
